import React, { useState, useEffect, useCallback } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { HistoryEntry } from "../types";
import { apiService } from "../services/api";

const History: React.FC = () => {
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadHistoryEntries = useCallback(async () => {
    try {
      const entries = await apiService.getHistory();
      const sortedEntries = (entries as HistoryEntry[]).sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );
      setHistoryEntries(sortedEntries);
    } catch (error) {
      console.error("Error loading history:", error);
      // Fallback to sample data
      setHistoryEntries(getSampleHistory());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistoryEntries();
  }, [loadHistoryEntries]);

  const getSampleHistory = (): HistoryEntry[] => [
    {
      id: "1",
      title: "Foundation of All Saints Church",
      year: "1855",
      period: "Victorian Era",
      content:
        "All Saints Church, Thirsk was founded in 1855 during the Victorian era as part of the growing Catholic revival in North Yorkshire. The church was established to serve the spiritual needs of the local Catholic community in Thirsk and surrounding areas.",
      excerpt:
        "Founded in 1855 during the Victorian Catholic revival in North Yorkshire.",
      imageUrl:
        "https://images.unsplash.com/photo-1520637836862-4d197d17c93a?ixlib=rb-4.0.3&w=800&h=600&fit=crop",
    },
    {
      id: "2",
      title: "Early Community Growth",
      year: "1870-1900",
      period: "Late Victorian Period",
      content:
        "During the late Victorian period, the parish grew steadily as Catholic families settled in Thirsk. The church became a cornerstone of the local Catholic community, providing not only spiritual guidance but also social support and education for Catholic children.",
      excerpt:
        "Steady growth of the Catholic community in Thirsk during the late Victorian era.",
      imageUrl:
        "https://images.unsplash.com/photo-1491032459657-8f9991ac7a1b?ixlib=rb-4.0.3&w=800&h=600&fit=crop",
    },
    {
      id: "3",
      title: "Wartime Service",
      year: "1914-1945",
      period: "World Wars Era",
      content:
        "Throughout both World Wars, All Saints Church served as a beacon of hope and comfort for the community. The church provided support for families during difficult times, held special prayers for those serving in the military, and offered practical assistance to those in need.",
      excerpt:
        "The church served as a source of comfort and support during both World Wars.",
      imageUrl:
        "https://images.unsplash.com/photo-1500628550463-c8881a54d4d4?ixlib=rb-4.0.3&w=800&h=600&fit=crop",
    },
    {
      id: "4",
      title: "Modern Renewal",
      year: "1960-1980",
      period: "Vatican II Era",
      content:
        "Following the Second Vatican Council, All Saints Church embraced the spirit of renewal and modernization. The liturgy was celebrated in English, greater lay participation was encouraged, and the church became more actively involved in ecumenical and social justice activities in the Thirsk community.",
      excerpt:
        "Post-Vatican II renewal brought modernization and increased community engagement.",
      imageUrl:
        "https://images.unsplash.com/photo-1529448005898-b19fc13465b7?ixlib=rb-4.0.3&w=800&h=600&fit=crop",
    },
    {
      id: "5",
      title: "Recent Developments",
      year: "2000-Present",
      period: "Contemporary Era",
      content:
        "In recent decades, All Saints Church has continued to adapt to serve the changing needs of the Thirsk community. The parish has embraced new technologies, developed vibrant youth and family programs, and strengthened its commitment to social justice and environmental stewardship while maintaining its rich Catholic traditions.",
      excerpt:
        "Modern adaptations while preserving Catholic traditions and community service.",
      imageUrl:
        "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&w=800&h=600&fit=crop",
    },
  ];

  const toggleExpanded = (id: string) => {
    setExpandedEntry(expandedEntry === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-church-light py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Our History
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover the rich heritage of All Saints Church, Thirsk, and how our
            parish has served the Catholic community for over 160 years.
          </p>
        </header>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-church-blue mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading our history...</p>
          </div>
        ) : (
          <>
            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div
                className="absolute left-4 md:left-1/2 md:transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-church-blue"
                aria-hidden="true"
              ></div>

              <div className="space-y-8">
                {historyEntries.map((entry, index) => (
                  <div
                    key={entry.id}
                    className={`relative flex items-start ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    {/* Timeline marker */}
                    <div
                      className="absolute left-4 md:left-1/2 md:transform md:-translate-x-1/2 w-4 h-4 bg-church-gold rounded-full border-4 border-white shadow-md"
                      aria-hidden="true"
                    ></div>

                    {/* Content */}
                    <div
                      className={`ml-12 md:ml-0 ${index % 2 === 0 ? "md:mr-auto md:pr-8" : "md:ml-auto md:pl-8"} md:w-1/2`}
                    >
                      <article className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
                        {entry.imageUrl && (
                          <img
                            src={entry.imageUrl}
                            alt={entry.period}
                            className="w-full h-48 object-cover"
                          />
                        )}

                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <span className="inline-block bg-church-blue text-white text-xs font-medium px-2 py-1 rounded-full mb-2">
                                {entry.year}
                              </span>
                              <h3 className="text-xl font-serif font-bold text-church-dark">
                                {entry.title}
                              </h3>
                              <p className="text-sm text-gray-500 font-medium">
                                {entry.period}
                              </p>
                            </div>
                            <button
                              onClick={() => toggleExpanded(entry.id)}
                              className="text-church-blue hover:text-blue-800 p-2 rounded focus:outline-none focus:ring-2 focus:ring-church-blue"
                              aria-expanded={expandedEntry === entry.id}
                              aria-label={`${expandedEntry === entry.id ? "Collapse" : "Expand"} details for ${entry.title}`}
                            >
                              {expandedEntry === entry.id ? (
                                <ChevronUpIcon className="h-5 w-5" />
                              ) : (
                                <ChevronDownIcon className="h-5 w-5" />
                              )}
                            </button>
                          </div>

                          <div className="space-y-4">
                            <p className="text-gray-600">{entry.excerpt}</p>

                            {expandedEntry === entry.id && (
                              <div className="border-t border-gray-200 pt-4">
                                <p className="text-gray-700 leading-relaxed">
                                  {entry.content}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </article>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sources */}
            <div className="mt-16">
              <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
                <h3 className="text-xl font-serif font-bold text-church-dark mb-4">
                  Sources
                </h3>
                <p className="text-gray-600 mb-4">
                  Our historical information is drawn from the following
                  sources:
                </p>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="https://taking-stock.org.uk/building/thirsk-all-saints/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-church-blue hover:text-blue-800 underline"
                    >
                      Taking Stock - All Saints Catholic Church, Thirsk
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://familyofsites.bishopsconference.org.uk/wp-content/uploads/sites/8/2019/07/All%20Saints%20Thirsk.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-church-blue hover:text-blue-800 underline"
                    >
                      An analysis of the importance of All Saints, its
                      historical background, setting and description of the
                      building
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-8 text-center">
              <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl mx-auto">
                <h3 className="text-2xl font-serif font-bold text-church-dark mb-4">
                  Be Part of Our Continuing Story
                </h3>
                <p className="text-gray-600 mb-6">
                  Our history continues to be written through the faith and
                  service of our parishioners. Join us as we build upon our rich
                  heritage and serve the Thirsk community together.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/team"
                    className="bg-church-blue hover:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
                  >
                    Meet Our Team
                  </a>
                  <a
                    href="mailto:allsaintsthirsk@rcdmidd.org.uk"
                    className="border-2 border-church-blue text-church-blue hover:bg-church-blue hover:text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-church-blue focus:ring-offset-2"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default History;
